/**
 * PATH: packages/email-hub/src/feature/components/sender-identity/VendorIdentity.tsx
 * FILE_ID: EMAILHUB-SENDERIDENTITY-VENDOR-001
 * PURPOSE: Render the AP-side (supplier / subscription) identity states — the
 *          saved vendor badge, and the unfiled vendor with ONE inferred filing
 *          verb plus the other kind behind a ▾ menu.
 * ROLE IN ARCHITECTURE: Presentational leaf of the sender-identity module.
 * MAIN EXPORTS: SavedVendorIdentity, UnfiledVendorIdentity.
 * NON-RESPONSIBILITIES: No gating (predicates.ts), no mutations (actions hook).
 *
 * NOTES FOR FUTURE AI: Do NOT restore two equal-weight vendor buttons. Showing
 * "Add as Supplier" and "Add as Subscription" side by side asked the operator a
 * question the classifier had already answered — on an Anthropic receipt already
 * chipped "SaaS vendor" (2026-07-25). The primary verb now follows
 * ``inferredVendorKind``; the other kind stays reachable in the ▾ menu, because
 * the classifier is a guess and a wrong guess must remain one click from fixed.
 * Both kinds still POST the same idempotent create-sender-supplier endpoint.
 */
import { useEffect, useRef } from 'react'
import { Building2, CheckCircle2, ChevronDown, Loader2, Repeat, UserPlus } from 'lucide-react'

import { cn } from '../../../_internal/lib/utils'
import type { VendorKind } from './predicates'
import type { SenderIdentityActions } from './useSenderIdentityActions'

/** Operator-facing copy per vendor kind. "File as …" (not "Add as …") because
 *  the operator reads this strip as a filing desk: the sender already exists,
 *  the question is which drawer it goes in. */
const VENDOR_COPY: Record<
  VendorKind,
  { verb: string; pending: string; alt: string; hint: string }
> = {
  subscription: {
    verb: 'File as subscription',
    pending: 'Filing…',
    alt: 'as supplier instead',
    hint: 'Recurring SaaS / membership / licence — feeds the Recurring spend view',
  },
  supplier: {
    verb: 'File as supplier',
    pending: 'Filing…',
    alt: 'as subscription instead',
    hint: 'One-off trade supplier — files into the supplier master',
  },
}

const OTHER_KIND: Record<VendorKind, VendorKind> = {
  subscription: 'supplier',
  supplier: 'subscription',
}

interface SavedVendorIdentityProps {
  savedName: string
  kind: VendorKind
  /** Flat chip (lane) vs bordered pill (standalone reader). */
  flat: boolean
  pad: string
  className?: string
}

/** Filed vendor: identity only, no CTA. Violet + repeat glyph for a
 *  subscription so recurring overhead is distinguishable at a glance from a
 *  one-off trade supplier (FEAT-2026-06-21-SUBSCRIPTION-VENDOR). */
export function SavedVendorIdentity({
  savedName,
  kind,
  flat,
  pad,
  className,
}: SavedVendorIdentityProps) {
  const subscription = kind === 'subscription'
  const Glyph = subscription ? Repeat : CheckCircle2
  const tone = subscription
    ? 'text-violet-700 dark:text-violet-300'
    : 'text-emerald-700 dark:text-emerald-300'

  // Lane chip: name first, kind as a quiet suffix — the lane's own caption
  // already says what column this is, so a loud "Supplier" label repeats it.
  if (flat) {
    return (
      <div
        className={cn('flex items-center gap-1.5 min-w-0', tone, className)}
        data-testid="sender-identity-card"
        data-resolved="false"
        data-supplier="saved"
        data-supplier-kind={kind}
      >
        <Glyph className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="truncate font-medium">{savedName}</span>
        <span className="shrink-0 text-[10.5px] opacity-70">
          {subscription ? 'subscription' : 'supplier'}
        </span>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'flex items-center gap-2 flex-wrap rounded-lg border',
        pad,
        subscription
          ? 'border-violet-200 dark:border-violet-900/50 bg-violet-50 dark:bg-violet-900/20'
          : 'border-emerald-200 dark:border-emerald-900/50 bg-emerald-50 dark:bg-emerald-900/20',
        className,
      )}
      data-testid="sender-identity-card"
      data-resolved="false"
      data-supplier="saved"
      data-supplier-kind={kind}
    >
      <Glyph
        className={cn(
          'h-4 w-4 shrink-0',
          subscription
            ? 'text-violet-600 dark:text-violet-400'
            : 'text-emerald-600 dark:text-emerald-400',
        )}
        aria-hidden
      />
      <span
        className={cn(
          'font-medium',
          subscription
            ? 'text-violet-800 dark:text-violet-200'
            : 'text-emerald-800 dark:text-emerald-200',
        )}
      >
        {subscription ? 'Subscription' : 'Supplier'}
      </span>
      <span className={cn('inline-flex items-center gap-1', tone)}>
        <Building2 className="h-3.5 w-3.5 shrink-0" aria-hidden /> {savedName}
      </span>
    </div>
  )
}

interface UnfiledVendorIdentityProps {
  vendorLabel: string
  /** What the classifier already believes this sender is — drives the single
   *  primary verb. See predicates.inferredVendorKind. */
  kind: VendorKind
  actions: SenderIdentityActions
  flat: boolean
  pad: string
  className?: string
}

/** Unfiled vendor: name + ONE primary verb + ▾ for the other kind. */
export function UnfiledVendorIdentity({
  vendorLabel,
  kind,
  actions,
  flat,
  pad,
  className,
}: UnfiledVendorIdentityProps) {
  const copy = VENDOR_COPY[kind]
  const other = OTHER_KIND[kind]
  const menuOpen = actions.roleMenuOpen
  const wrapRef = useRef<HTMLDivElement | null>(null)

  // Dismiss on outside click: the menu holds the only path to the non-inferred
  // kind, so it must never strand the operator in an open overlay.
  useEffect(() => {
    if (!menuOpen) return
    function onDown(ev: MouseEvent) {
      if (!wrapRef.current?.contains(ev.target as Node)) actions.setRoleMenuOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [menuOpen, actions])

  return (
    <div
      ref={wrapRef}
      className={cn(
        'relative flex items-center gap-1.5 min-w-0',
        flat ? null : cn('flex-wrap rounded-lg border border-border bg-muted/40', pad),
        className,
      )}
      data-testid="sender-identity-card"
      data-resolved="false"
      data-supplier="true"
      data-vendor-kind={kind}
    >
      <Building2
        className={cn('shrink-0 text-muted-foreground', flat ? 'h-3.5 w-3.5' : 'h-4 w-4')}
        aria-hidden
      />
      {/* Name claims leftover width; the CTA is shrink-0 and must stay short
          in flat/lane mode so "Anthropic" is never crushed to "Anthr…". */}
      <span
        className={cn(
          'truncate font-medium text-foreground/90',
          flat && 'min-w-0 flex-1',
        )}
      >
        {vendorLabel}
      </span>
      <button
        type="button"
        onClick={() => actions.addVendor(kind)}
        disabled={actions.busy}
        title={copy.hint}
        aria-label={copy.verb}
        data-testid="vendor-file-primary"
        className={cn(
          'shrink-0 inline-flex items-center gap-1 rounded-md font-medium text-white transition-colors disabled:opacity-60',
          kind === 'subscription'
            ? 'bg-violet-600 hover:bg-violet-700'
            : 'bg-indigo-600 hover:bg-indigo-700',
          flat ? 'px-1.5 py-0.5 text-[10.5px]' : 'px-2.5 py-1 text-xs',
        )}
      >
        {actions.pendingKind === kind ? (
          <Loader2 className={cn('animate-spin', flat ? 'h-3 w-3' : 'h-3.5 w-3.5')} aria-hidden />
        ) : kind === 'subscription' ? (
          <Repeat className={cn(flat ? 'h-3 w-3' : 'h-3.5 w-3.5')} aria-hidden />
        ) : (
          <UserPlus className={cn(flat ? 'h-3 w-3' : 'h-3.5 w-3.5')} aria-hidden />
        )}
        {/* Flat/lane: short "File" so the vendor name keeps the column.
            Standalone keeps the full "File as subscription" verb. */}
        {actions.pendingKind === kind
          ? flat
            ? '…'
            : copy.pending
          : flat
            ? 'File'
            : copy.verb}
      </button>
      {/* The classifier's guess is a guess. One click to the other kind. */}
      <button
        type="button"
        onClick={() => actions.setRoleMenuOpen(!menuOpen)}
        disabled={actions.busy}
        title={`Not a ${kind}? File it ${copy.alt}`}
        aria-label="Other filing options"
        aria-expanded={menuOpen}
        data-testid="vendor-file-menu-toggle"
        className={cn(
          'shrink-0 inline-flex items-center justify-center rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50',
          flat ? 'h-[18px] w-[18px]' : 'h-6 w-6',
        )}
      >
        <ChevronDown className="h-3 w-3" aria-hidden />
      </button>
      {menuOpen && (
        <div
          className="absolute left-0 top-full z-30 mt-1 min-w-[190px] rounded-md border border-border bg-popover p-1 shadow-md"
          data-testid="vendor-file-menu"
        >
          <button
            type="button"
            onClick={() => actions.addVendor(other)}
            disabled={actions.busy}
            title={VENDOR_COPY[other].hint}
            data-testid="vendor-file-alt"
            className="flex w-full items-center gap-1.5 rounded px-2 py-1 text-left text-[11px] text-foreground/90 hover:bg-muted transition-colors disabled:opacity-50"
          >
            {actions.pendingKind === other ? (
              <Loader2 className="h-3 w-3 animate-spin shrink-0" aria-hidden />
            ) : other === 'subscription' ? (
              <Repeat className="h-3 w-3 shrink-0" aria-hidden />
            ) : (
              <UserPlus className="h-3 w-3 shrink-0" aria-hidden />
            )}
            File {copy.alt}
          </button>
        </div>
      )}
      {actions.error && (
        <span className="text-destructive text-[10.5px] shrink-0">{actions.error}</span>
      )}
    </div>
  )
}
